import { Box, Pressable, ScrollView, SelectInput, SelectList } from "../../../../../../../mads";
import { useI18n } from "../../../../../../../shared/I18n";

import React, { useMemo, useState } from "react";
import { Modal, StyleSheet } from "react-native";

const COMMUTE_TIME_OPTIONS = [15, 25, 35, 45, 60, 90] as const;
type CommuteTimeMinutes = (typeof COMMUTE_TIME_OPTIONS)[number];

export interface MaxCommuteSelectProps {
  value?: number;
  onChange: (value: CommuteTimeMinutes) => void;
  disabled?: boolean;
  /**
   * When true, render the dropdown as an inline popover anchored to the
   * SelectInput (legacy desktop UX). When false/undefined, render a
   * full-screen centered Modal (mobile sheet UX).
   */
  isDesktopDisplay?: boolean;
}

export function MaxCommuteSelect({
  value,
  onChange,
  disabled,
  isDesktopDisplay,
}: MaxCommuteSelectProps) {
  const gt = useI18n();
  const [isOpen, setIsOpen] = useState(false);

  const items = useMemo(
    () =>
      COMMUTE_TIME_OPTIONS.map((minutes) => ({
        text: gt.gettext("Up to {0} minutes", [minutes]),
        selected: value === minutes,
        onPress: () => {
          onChange(minutes);
          setIsOpen(false);
        },
      })),
    [gt, value, onChange],
  );

  const selectedLabel = value !== undefined ? gt.gettext("Up to {0} minutes", [value]) : undefined;

  const selectInput = (
    <SelectInput
      labelText={gt.gettext("Max commute time")}
      placeholderText={gt.gettext("Select an option")}
      text={selectedLabel}
      disabled={disabled}
      expanded={isOpen}
      onPress={() => setIsOpen(!isOpen)}
    />
  );

  if (isDesktopDisplay) {
    return (
      <Box sx={styles.desktopAnchor}>
        {selectInput}
        {isOpen && (
          <>
            {/*
             * Invisible full-screen backdrop closes the popover on
             * outside clicks. Anchored via position:fixed so it
             * covers the viewport regardless of the SelectInput's
             * stacking context.
             */}
            <Pressable
              sx={styles.desktopBackdrop}
              onPress={() => setIsOpen(false)}
              accessible={false}
            />
            <Box sx={styles.desktopPopover}>
              <ScrollView>
                <SelectList items={items} />
              </ScrollView>
            </Box>
          </>
        )}
      </Box>
    );
  }

  return (
    <>
      {selectInput}
      <Modal
        visible={isOpen}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setIsOpen(false)}
      >
        <Box sx={styles.overlayRoot}>
          <Pressable sx={styles.overlay} onPress={() => setIsOpen(false)} accessible={false} />
          <Box sx={styles.dialog}>
            <ScrollView>
              <SelectList items={items} />
            </ScrollView>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  overlayRoot: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  overlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.4)",
  },
  dialog: {
    backgroundColor: "white",
    borderRadius: 12,
    maxHeight: "80%",
    width: "100%",
    overflow: "hidden",
  },
  desktopAnchor: {
    // `position: relative` anchors the absolute-positioned popover below
    // to this wrapper so it appears right under the SelectInput.
    position: "relative",
  },
  desktopBackdrop: {
    // @ts-expect-error - position 'fixed' is valid on web (RNW)
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    // Transparent — the legacy native <select> doesn't dim, and the modal
    // already has its own backdrop behind it.
    backgroundColor: "transparent",
    // Below the popover, above the rest of the modal so outside clicks
    // close it before triggering anything else.
    zIndex: 1,
  },
  desktopPopover: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    marginTop: 4,
    backgroundColor: "white",
    borderRadius: 8,
    maxHeight: 285,
    overflow: "hidden",
    // Standard popover shadow
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 12,
    elevation: 8,
    borderWidth: 1,
    borderColor: "#e0e0e0",
    zIndex: 2,
  },
});
